// Strict phone validation utilities.
// Works with BOTH input formats used across the app:
//   • digits-only (e.g. "966501234567")           ← react-phone-input-2 output
//   • "+"-prefixed (e.g. "+966501234567")          ← MultiPhoneInput rows
//
// Public API:
//   normalizePhone(input)     → cleaned string, Latin digits only
//   splitPhone(input)         → { dial, local }  (dial without "+", local digits only)
//   validatePhone(input)      → { ok, reason, formatted, dial, local, expectedLen }
//   maxLengthForDial(dial)    → int (defaults to 15 for unknown dials)
//   placeholderForDial(dial)  → localised placeholder
//   clampLocalDigits(dial,d)  → digits sliced to the exact/max length for the dial

import {
  EXACT_LOCAL_DIGITS,
  COUNTRY_LABELS_AR,
  OTHER_DIALS,
} from "../constants/phoneRules";

// Arabic-Indic + Persian digits → Latin.
const DIGIT_MAP = {
  "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4",
  "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9",
  "۰": "0", "۱": "1", "۲": "2", "۳": "3", "۴": "4",
  "۵": "5", "۶": "6", "۷": "7", "۸": "8", "۹": "9",
};

const ALL_DIALS = [...Object.keys(EXACT_LOCAL_DIGITS), ...OTHER_DIALS];
// Longest first to prevent "+966" matching before "+9665..."-like inputs.
const SORTED_DIALS = [...new Set(ALL_DIALS)].sort((a, b) => b.length - a.length);

export function toLatinDigits(input) {
  return String(input || "").split("").map((c) => DIGIT_MAP[c] ?? c).join("");
}

export function normalizePhone(input) {
  if (!input) return "";
  let s = toLatinDigits(input).replace(/[\s\-()]/g, "");
  s = s.replace(/[^\d+]/g, "");
  // "00xxx" → "+xxx"
  if (s.startsWith("00")) s = "+" + s.slice(2);
  // Multiple "+" → keep the last one (last-written wins).
  const pluses = (s.match(/\+/g) || []).length;
  if (pluses > 1) s = s.slice(s.lastIndexOf("+"));
  else if (!s.startsWith("+")) s = s.replace(/\+/g, "");
  return s;
}

/**
 * Split any phone into { dial, local }.
 *  - `dial` is the country code WITHOUT the leading "+"  (e.g. "966")
 *  - `local` is the remaining digits, Latin only
 *  Empty strings if we cannot detect a dial.
 */
export function splitPhone(input) {
  const s = normalizePhone(input);
  if (!s) return { dial: "", local: "" };

  const digitsOnly = s.replace(/\D/g, "");

  // If input starts with "+", strip and match dial at the very start.
  if (s.startsWith("+")) {
    for (const dial of SORTED_DIALS) {
      if (digitsOnly.startsWith(dial)) {
        return { dial, local: digitsOnly.slice(dial.length) };
      }
    }
    return { dial: "", local: digitsOnly };
  }

  // No leading "+": look for a known dial at the start of the digits.
  //  • Strict-rule dials (SA/AE/KW/OM/QA) are recognised immediately — even
  //    with an incomplete local part — so validation fires while typing.
  //  • Loose-rule dials require at least 6 local digits to avoid partial
  //    matches during typing.
  for (const dial of SORTED_DIALS) {
    if (!digitsOnly.startsWith(dial)) continue;
    if (EXACT_LOCAL_DIGITS[dial] !== undefined) {
      return { dial, local: digitsOnly.slice(dial.length) };
    }
    if (digitsOnly.length >= dial.length + 6) {
      return { dial, local: digitsOnly.slice(dial.length) };
    }
  }
  return { dial: "", local: digitsOnly };
}

export function maxLengthForDial(dial) {
  const key = String(dial || "").replace(/\D/g, "");
  return EXACT_LOCAL_DIGITS[key] ?? 15;
}

export function clampLocalDigits(dial, digits) {
  const max = maxLengthForDial(dial);
  return String(digits || "").replace(/\D/g, "").slice(0, max);
}

export function placeholderForDial(dial, lang = "ar") {
  const key = String(dial || "").replace(/\D/g, "");
  const expected = EXACT_LOCAL_DIGITS[key];
  if (!expected) return lang === "ar" ? "رقم الجوال" : "Phone number";
  return lang === "ar" ? `${expected} أرقام` : `${expected} digits`;
}

/**
 * Strict validation.
 * Returns { ok, reason, formatted } — `formatted` is "+"-prefixed E.164.
 */
export function validatePhone(input) {
  const s = normalizePhone(input);
  if (!s || s === "+") {
    return { ok: false, reason: "رقم الجوال مطلوب", formatted: "" };
  }
  const { dial, local } = splitPhone(s);
  const digitsOnly = local.replace(/\D/g, "");

  if (!dial) {
    // No detected country — enforce loose 7–15 window.
    const bare = s.replace(/\D/g, "");
    if (bare.length < 7) {
      return { ok: false, reason: "رقم قصير جداً — أضف مفتاح الدولة", formatted: s };
    }
    if (bare.length > 16) {
      return { ok: false, reason: "رقم طويل جداً", formatted: s };
    }
    return { ok: true, reason: "", formatted: s.startsWith("+") ? s : `+${bare}` };
  }

  // Strict rule?
  const expected = EXACT_LOCAL_DIGITS[dial];
  if (expected !== undefined && digitsOnly.length !== expected) {
    const country = COUNTRY_LABELS_AR[dial] || dial;
    return {
      ok: false,
      reason: `رقم ${country} يجب أن يكون ${expected} أرقام بالضبط (الحالي: ${digitsOnly.length})`,
      formatted: `+${dial}${digitsOnly}`,
      dial,
      local: digitsOnly,
      expectedLen: expected,
    };
  }

  // Loose window for others (6–15 local digits).
  if (expected === undefined) {
    if (digitsOnly.length < 6) {
      return { ok: false, reason: "رقم قصير جداً", formatted: `+${dial}${digitsOnly}` };
    }
    if (digitsOnly.length > 15) {
      return { ok: false, reason: "رقم طويل جداً", formatted: `+${dial}${digitsOnly}` };
    }
  }

  return {
    ok: true,
    reason: "",
    formatted: `+${dial}${digitsOnly}`,
    dial,
    local: digitsOnly,
    expectedLen: expected,
  };
}

/** Validate a list of phones (MultiPhoneInput rows). Returns first-error message or null. */
export function validatePhoneList(rows, { requirePrimary = true } = {}) {
  const arr = Array.isArray(rows) ? rows : [];
  if (arr.length === 0) return "رقم الجوال مطلوب";
  const nonEmpty = arr.filter((r) => (r?.number || "").replace(/\D/g, "").length > 3);
  if (requirePrimary && nonEmpty.length === 0) return "رقم الجوال مطلوب";
  for (let i = 0; i < arr.length; i++) {
    const r = arr[i];
    const digits = (r?.number || "").replace(/\D/g, "");
    if (digits.length <= 3) continue; // Empty (just the dial) — skip.
    const check = validatePhone(r.number);
    if (!check.ok) return `الرقم رقم ${i + 1}: ${check.reason}`;
  }
  return null;
}
